import {
  BuildingOffice2Icon,
  CalendarIcon,
  ExclamationTriangleIcon,
  HomeIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import { useMemo } from 'react';

import Card from 'components/Card';
import { RosterRequestQuery } from 'generated/graphql';
import { useAppSelector } from 'store/store';

type TravelerAssignmentViewProps = {
  request?: RosterRequestQuery['rosterRequest'];
};

/**
 * Simplified view for travelers showing only their own assignment.
 * Hides pricing, other guests, and admin-only information.
 */
const TravelerAssignmentView = ({ request }: TravelerAssignmentViewProps) => {
  const clientTeamMemberId = useAppSelector(
    (state) => state.user.profile?.clientTeamMemberId,
  );

  // Find the traveler's own assignment
  const myAssignment = useMemo(() => {
    if (!clientTeamMemberId) return null;
    return request?.rosterRoomAssignments?.find(
      (assignment) =>
        String(assignment.guest.id) === String(clientTeamMemberId),
    );
  }, [clientTeamMemberId, request?.rosterRoomAssignments]);

  // Find roommates (same hotel and dates)
  const roommates = useMemo(() => {
    if (!myAssignment || !request?.rosterRoomAssignments) return [];
    return request.rosterRoomAssignments.filter((assignment) => {
      if (String(assignment.guest.id) === String(clientTeamMemberId))
        return false;
      const sameHotel =
        ('databaseId' in myAssignment.hotel && 'databaseId' in assignment.hotel
          ? myAssignment.hotel.databaseId === assignment.hotel.databaseId
          : false) ||
        ('id' in myAssignment.hotel && 'id' in assignment.hotel
          ? myAssignment.hotel.id === assignment.hotel.id
          : false);
      const sameDates =
        myAssignment.checkInDate === assignment.checkInDate &&
        myAssignment.checkOutDate === assignment.checkOutDate;
      return sameHotel && sameDates;
    });
  }, [myAssignment, request?.rosterRoomAssignments, clientTeamMemberId]);

  // Traveler not linked to a team member
  if (!clientTeamMemberId) {
    return (
      <Card>
        <div className="flex items-center gap-3 text-yellow-700">
          <ExclamationTriangleIcon className="size-6" />
          <p>
            Your account is not linked to a team member. Please contact your
            administrator.
          </p>
        </div>
      </Card>
    );
  }

  // Traveler not assigned to this request
  if (!myAssignment) {
    return (
      <Card>
        <div className="flex items-center gap-3 text-gray-600">
          <UserGroupIcon className="size-6" />
          <p>You are not assigned to this group request.</p>
        </div>
      </Card>
    );
  }

  const hotelName =
    'name' in myAssignment.hotel ? myAssignment.hotel.name : 'Hotel';

  return (
    <Card>
      <h2 className="font-oswald text-2xl font-bold uppercase text-secondaryBlue">
        Your Assignment
      </h2>

      <div className="mt-6 space-y-4">
        {/* Hotel */}
        <div className="flex items-start gap-3">
          <BuildingOffice2Icon className="size-5 shrink-0 text-gray-400" />
          <div>
            <p className="font-medium text-gray-900">{hotelName}</p>
            {'address' in myAssignment.hotel && myAssignment.hotel.address && (
              <p className="text-sm text-gray-500">
                {[
                  myAssignment.hotel.address.city,
                  myAssignment.hotel.address.state,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </p>
            )}
          </div>
        </div>

        {/* Dates */}
        <div className="flex items-start gap-3">
          <CalendarIcon className="size-5 shrink-0 text-gray-400" />
          <div>
            <p className="font-medium text-gray-900">
              {format(parseISO(myAssignment.checkInDate), 'MMM d, yyyy')} -{' '}
              {format(parseISO(myAssignment.checkOutDate), 'MMM d, yyyy')}
            </p>
            <p className="text-sm text-gray-500">Check-in / Check-out</p>
          </div>
        </div>

        {/* Room Info */}
        <div className="flex items-start gap-3">
          <HomeIcon className="size-5 shrink-0 text-gray-400" />
          <div>
            <p className="font-medium text-gray-900">Room Assignment</p>
            <p className="text-sm text-gray-500">Confirmed</p>
          </div>
        </div>

        {/* Roommates */}
        {roommates.length > 0 && (
          <div className="flex items-start gap-3">
            <UserGroupIcon className="size-5 shrink-0 text-gray-400" />
            <div>
              <p className="font-medium text-gray-900">Roommates</p>
              <ul className="mt-1 space-y-1">
                {roommates.map((roommate) => (
                  <li key={roommate.id} className="text-sm text-gray-500">
                    {roommate.guest.firstName} {roommate.guest.lastName}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default TravelerAssignmentView;
